import { GITHUB_APP_URL } from '../settings';

/**
 * Opens the GitHub App page in a popup and tells you when it closes.
 *
 * Extracted from MessageContainer, which owned the only copy: two screens send
 * people to grant access now — the empty repository list, and the app whose
 * deploy failed because the installation was revoked — and a second copy of
 * the dual-screen positioning maths is not something to keep in sync by hand.
 *
 * `onClosed` fires when the window goes away, and that is all it can honestly
 * report. The popup lands back on the dashboard's own callback route, which
 * imports the installation and closes itself, but it also closes when the
 * person gives up and clicks the X — the two are indistinguishable from here.
 * Callers must therefore VERIFY rather than assume: retry the thing that
 * failed and let it fail again if nothing was fixed, instead of reporting
 * success because a window disappeared.
 */

const WINDOW_NAME = 'GithubIntegrationWindow';

let openWindow: Window | null = null;

export function openGithubPermissions(options?: {
  width?: number;
  height?: number;
  onClosed?: () => void;
}): void {
  const width = (options && options.width) || 800;
  const height = (options && options.height) || 600;

  if (openWindow && !openWindow.closed) {
    openWindow.focus();
    return;
  }

  // Dual-screen: `screenLeft`/`screenTop` are where THIS window sits, which is
  // not the origin on a second monitor.
  const dualScreenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY;

  const outerWidth = window.innerWidth || document.documentElement.clientWidth;
  const outerHeight =
    window.innerHeight || document.documentElement.clientHeight;

  const systemZoom = outerWidth / window.screen.availWidth;
  const left = (outerWidth - width) / 2 / systemZoom + dualScreenLeft;
  const top = (outerHeight - height) / 2 / systemZoom + dualScreenTop;

  openWindow = window.open(
    GITHUB_APP_URL,
    WINDOW_NAME,
    `scrollbars=yes,width=${width / systemZoom},height=${
      height / systemZoom
    },top=${top},left=${left}`
  );

  if (typeof window.focus === 'function' && openWindow) {
    openWindow.focus();
  }

  if (!options || !options.onClosed) {
    return;
  }

  const { onClosed } = options;

  // Polling, because a popup on another origin fires no event we can hear and
  // `window.opener` messaging would have to survive GitHub's own redirects.
  // A popup blocked outright never opens, so treat that as closed immediately
  // rather than leaving the caller waiting on an interval that never fires.
  if (!openWindow) {
    onClosed();
    return;
  }

  const watched = openWindow;
  const interval = window.setInterval(() => {
    if (watched.closed) {
      window.clearInterval(interval);
      onClosed();
    }
  }, 500);
}
